import {Injectable} from '@angular/core';
import {ApiService} from './api.service';
import {Deal} from "../model/deal.model";
import {indexNotFound, isDefined, utf8Decode} from "sdv-common";
import {DealGroup} from "../model/deal-group.model";
import {retryAfterMillis} from "../shared/constants";

@Injectable({providedIn: 'root'})
export class DealService {

  public favoriteAttrId = 1;
  public colorAttrId = 2;
  public pinnedAttrId = 3;
  public notificationAttrId = 4;
  public inOrderAttrId = 5;
  public outOrderAttrId = 6;
  public hiddenAttrId = 7;
  public commentAttrId = 8;
  public tagAttrId = 9;

  private deals: Deal[] = [];
  private groups: DealGroup[] = [];

  constructor(private apiService: ApiService) {
    if (this.apiService.isAuthorized()) {
      this.load();
    }
  }

  load() {
    this.apiService.get('deals')
      .subscribe(response => {
        let groups = [];
        for (const res of response) {
          let deal = this.convertDeal(res);
          let key = this.createKey(deal);
          let group = groups.find(g => g.key === key);
          if (!isDefined(group)) {
            group = this.prepareGroup(key);
            groups.push(group);
          }
          group.deals.push(deal);
        }
        this.groups = groups;
      }, err => {
        this.apiService.error({}, err);
        setTimeout(() => this.load(), retryAfterMillis);
      });
  }

  private prepareGroup(key): DealGroup {
    let group: DealGroup = {
      key,
      toToken: () => {
        return group.deals[0].toToken.symbol;
      },
      fromToken: () => {
        return group.deals[0].fromToken.symbol;
      },
      profit: () => {
        return group.deals.map(item => item.profit).reduce((prev, next) => prev + next);
      },
      price: () => {
        return group.totalSum() / group.quantity();
      },
      totalSum: () => {
        return group.deals.map(item => item.price * item.quantity).reduce((prev, next) => prev + next);
      },
      quantity: () => {
        return group.deals.map(item => item.quantity).reduce((prev, next) => prev + next);
      },
      expandGroup: false,
      deals: []
    };
    return group;
  }

  private convertDeal(dto) {
    let deal = {
      id: utf8Decode(dto.id || ''),
      created: dto.createdMillis || 0,
      fromToken: {
        id: utf8Decode(dto.fromToken.id || ''),
        contract: utf8Decode(dto.fromToken.contract || ''),
        name: utf8Decode(dto.fromToken.name || ''),
        symbol: utf8Decode(dto.fromToken.symbol || ''),
        network_id: dto.fromToken.networkId || 0,
        exchange_id: dto.fromToken.exchangeId || 0,
        decimal: dto.fromToken.decimal || 0,
        description: utf8Decode(dto.fromToken.description || ''),
        icon: utf8Decode(dto.fromToken.icon || '')
      },
      toToken: {
        id: utf8Decode(dto.toToken.id || ''),
        contract: utf8Decode(dto.toToken.contract || ''),
        name: utf8Decode(dto.toToken.name || ''),
        symbol: utf8Decode(dto.toToken.symbol || ''),
        network_id: dto.toToken.networkId || 0,
        exchange_id: dto.fromToken.exchangeId || 0,
        decimal: dto.fromToken.decimal || 0,
        description: utf8Decode(dto.toToken.description || ''),
        icon: utf8Decode(dto.toToken.icon || '')
      },
      direction: utf8Decode(dto.direction || ''),
      quantity: dto.quantity || 0,
      last_price: dto.lastPrice || 0,
      price: dto.price || 0,
      account_id: utf8Decode(dto.accountId || ''),
      folder_id: utf8Decode(dto.folderId || ''),
      selected: false,
      profit: 0,
      attributes: (dto.attributes || []).map(n => ({
        created: n.createdMillis || 0,
        id: utf8Decode(n.id || ''),
        value: n.value,
        code: n.code
      }))
    };
    if (deal.last_price > 0) {
      deal.profit = (deal.last_price * deal.quantity) - (deal.price * deal.quantity);
    }
    return deal;
  }

  getGroups() {
    return this.groups;
  }

  remove(dealId, price) {
    return new Promise((resolve, reject) => {
      this.apiService.delete('deals/' + dealId, {price})
        .subscribe(response => {
          let groupIndex = indexNotFound;
          let dealIndex = indexNotFound;
          for (let i = 0; i < this.groups.length; i++) {
            let group = this.groups[i];
            for (let j = 0; j < group.deals.length; j++) {
              if (group.deals[j].id === dealId) {
                groupIndex = i;
                dealIndex = j;
                break;
              }
            }
            if (groupIndex !== indexNotFound) {
              break;
            }
          }
          if (dealIndex !== indexNotFound) {
            this.groups[groupIndex].deals.splice(dealIndex, 1);
          }
          resolve({});
        }, err => {
          reject(err);
        });
    });
  }

  add(request) {
    return new Promise((resolve, reject) => {
      this.apiService.post('deals', request)
        .subscribe(dto => {
          const deal = this.convertDeal(dto);
          const key = this.createKey(deal);
          let group = this.groups.find(g => g.key === key);
          const groupFound = isDefined(group);
          if (!groupFound) {
            group = this.prepareGroup(key);
          }
          group.deals.push(deal);
          if (!groupFound) {
            this.groups.push(group);
          }
          Array.prototype.concat.apply([], this.getGroups().map(g => g.deals))
            .forEach(n => n.selected = false);
          group.expandGroup = true;
          deal.selected = true;
          resolve(deal.id);
        }, err => {
          reject(err);
        });
    });
  }

  edit(id, request) {
    return new Promise((resolve, reject) => {
      this.apiService.put('deals/' + id, request)
        .subscribe(dto => {
          const newDeal = this.convertDeal(dto);
          const newKey = this.createKey(newDeal);

          const oldDeal = Array.prototype.concat.apply([], this.getGroups().map(g => g.deals))
            .find(n => n.id === newDeal.id);
          const oldKey = this.createKey(oldDeal);

          if (newKey === oldKey) {
            oldDeal.account_id = newDeal.account_id;
            oldDeal.folder_id = newDeal.folder_id;
            oldDeal.quantity = newDeal.quantity;
            oldDeal.price = newDeal.price;
          } else {
            let newGroup = this.getGroups().find(g => g.key === newKey);
            const oldGroup = this.getGroups().find(g => g.key === oldKey);
            const oldIndexGroup = this.getGroups().findIndex(g => g.key === oldKey);
            const indexDeal = oldGroup.deals.findIndex(d => d.id === newDeal.id);

            oldGroup.deals.splice(indexDeal, 1);
            if (oldGroup.deals.length === 0) {
              this.getGroups().splice(oldIndexGroup, 1);
            }
            oldGroup.expandGroup = false;
            oldDeal.selected = true;

            if (!isDefined(newGroup)) {
              newGroup = this.prepareGroup(newKey);
              this.groups.push(newGroup);
            }
            newGroup.expandGroup = true;
            newDeal.selected = true;
            newGroup.deals.push(newDeal);
          }
          resolve({});
        }, err => {
          reject(err);
        });
    });
  }

  private createKey(deal: Deal) {
    return [
      deal.fromToken.contract,
      deal.toToken.contract,
      deal.account_id,
      deal.direction,
      deal.folder_id
    ].join();
  }
}
